import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { Info } from "lucide-react";
import {
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../components/utils";
import { Input, Label } from "../../components/shared/InputField";
import { MultiSelectV2 } from "../../components/shared/MultiSelectV2";
import { Select } from "../../components/shared/Select";
import { useEsopPlans } from "../../queries";
import { BsInputByDate, ExpenseReportDto } from "../reports/utils/report";
import { useReportJobStore } from "../../store/reportJobStore";
import { useCompanyStore } from "../../store";
import { HissaReportType, ScheduleParams } from "../../types/common";
import ScheduleSection from "../reports/dialogs/ScheduleSection";
import { useGetAssociatedUsers } from "../../queries/usersRoles";
import { useGetScheduledReports } from "../../queries/scheduledReports";
import DateFormatSelect from "../reports/dialogs/DateFormatSelect";
import {
  DateFormatKey,
  DEFAULT_DATE_FORMAT_KEY,
} from "../../utils/dateFormatCatalog";
import { useReportPiiMaskDefault } from "../../queries/siteSettings";

const CURRENCY_OPTIONS = [
  "INR - ₹",
  "USD - $",
  "EUR - €",
  "GBP - £",
  "SGD - S$",
  "AED - AED",
];

const EMPTY_BS_ROW: BsInputByDate = {
  grantDate: "",
  volatility: undefined,
  riskFreeRate: undefined,
  expectedDividends: undefined,
  attritionRate: undefined,
  sharePrice: undefined,
};

function ValuationLink() {
  const navigate = useNavigate();
  return (
    <div className="flex bg-[rgb(255,248,245)] rounded-md border-l-4 border-[#ff7a3d] p-2 border border-[#ff7a3d]">
      <Info
        size={8}
        className="w-10 mt-2 h-3 text-xs font-medium rounded cursor-pointer text-[#ff7a3d] px-0.5"
      />
      <p className="text-xs text-primaryText leading-normal m-0 p-1">
        Update Black-Scholes valuation data{" "}
        <button
          type="button"
          onClick={() => navigate("/options/create-valuation")}
          className="text-[#ff7a3d] underline hover:text-orange-600 bg-transparent border-none p-0 cursor-pointer"
        >
          here
        </button>
      </p>
    </div>
  );
}

const ExpenseReportDialog = ({
  onClose = () => {},
  data,
}: Readonly<{
  onClose: () => void;
  data: any;
}>) => {
  const { data: _plans } = useEsopPlans();
  const plans = _plans || [];
  const { upsertReport } = useReportJobStore();
  const { companyData } = useCompanyStore();
  const { data: associatedUsers } = useGetAssociatedUsers();
  const { refetch: refetchScheduledReports } = useGetScheduledReports();

  const planOptions = plans.map((p) => ({ value: p.planName }));

  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedPlanNames, setSelectedPlanNames] = useState<string[]>([]);
  const [currency, setCurrency] = useState("INR - ₹");
  const { maskPiiData, setMaskPiiData } = useReportPiiMaskDefault();
  const [dateFormat, setDateFormat] = useState<DateFormatKey>(
    DEFAULT_DATE_FORMAT_KEY
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [scheduleParams, setScheduleParams] = useState<
    ScheduleParams | undefined
  >(undefined);

  // Global BS parameters
  const [volatility, setVolatility] = useState("");
  const [riskFreeRate, setRiskFreeRate] = useState("");
  const [expectedDividends, setExpectedDividends] = useState("");
  const [attritionRate, setAttritionRate] = useState("");
  const [sharePrice, setSharePrice] = useState("");

  // Per-grant-date overrides
  const [bsRows, setBsRows] = useState<BsInputByDate[]>([]);

  const dateTypes = [
    "Current Financial Year",
    "Previous Financial Year",
    "Custom Interval",
  ];
  const [selectedDateType, setSelectedDateType] = useState("");

  const handleDateTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSelectedDateType(val);
    const date = new Date();
    if (val === "Current Financial Year") {
      setStartDate(`${date.getFullYear()}-04-01`);
      setEndDate(`${date.getFullYear() + 1}-03-31`);
    } else if (val === "Previous Financial Year") {
      setStartDate(`${date.getFullYear() - 1}-04-01`);
      setEndDate(`${date.getFullYear()}-03-31`);
    } else {
      setStartDate("");
      setEndDate("");
    }
  };

  const parseOptionalFloat = (val: string): number | undefined => {
    const n = parseFloat(val);
    return Number.isNaN(n) ? undefined : n;
  };

  const addBsRow = () => setBsRows((prev) => [...prev, { ...EMPTY_BS_ROW }]);

  const removeBsRow = (idx: number) =>
    setBsRows((prev) => prev.filter((_, i) => i !== idx));

  const updateBsRow = (
    idx: number,
    field: keyof BsInputByDate,
    value: string
  ) => {
    setBsRows((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        if (field === "grantDate") return { ...row, grantDate: value };
        return {
          ...row,
          [field]: value === "" ? undefined : parseFloat(value),
        };
      })
    );
  };

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      toast("Please select Period Start Date and Period End Date", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }
    if (scheduleParams && scheduleParams.recipients.length === 0) {
      toast("Please select at least one recipient to schedule the report", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    const planIds = plans
      .filter((p) => selectedPlanNames.includes(p.planName))
      .map((p) => p.esopPlanId);

    const validBsRows = bsRows.filter((r) => r.grantDate.trim() !== "");

    const payload: ExpenseReportDto = {
      startDate,
      endDate,
      planIds: planIds.length > 0 ? planIds : undefined,
      maskPiiData,
      currency: currency || undefined,
      volatility: parseOptionalFloat(volatility),
      riskFreeRate: parseOptionalFloat(riskFreeRate),
      expectedDividends: parseOptionalFloat(expectedDividends),
      attritionRate: parseOptionalFloat(attritionRate),
      sharePrice: parseOptionalFloat(sharePrice),
      bsInputsByDate: validBsRows.length > 0 ? validBsRows : undefined,
      dateFormat,
      scheduleParams:
        scheduleParams && scheduleParams.recipients.length > 0
          ? scheduleParams
          : undefined,
    };

    setLoading(true);
    try {
      await data?.onSubmitFunction(payload);
      if (scheduleParams && scheduleParams.recipients.length > 0) {
        refetchScheduledReports();
      }
      if (companyData) {
        const jobKey = HissaReportType.EXPENSE_REPORT;
        upsertReport(companyData.id, jobKey, {
          status: "QUEUED",
          updatedAt: Date.now(),
        });
      }
      toast(
        scheduleParams && scheduleParams.recipients.length > 0
          ? "Report scheduled successfully. Recipients will receive an email with the report."
          : "Your report has been queued. Come back to this page and click Download once it's ready.",
        { type: "info", autoClose: 3000 }
      );
      onClose();
    } catch (err: any) {
      toast(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.errors ||
          "Error while generating the report",
        { type: "error", autoClose: 2000 }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-[860px]">
      <div className="p-8 text-lg font-medium border-b">
        <h6 className="flex justify-between">
          {data?.headerName}
          <span className="cursor-pointer" onClick={onClose}>
            X
          </span>
        </h6>
      </div>

      <VStack className="w-full gap-4 px-8 py-4">
        {/* Date Section */}
        <HStack className="gap-6">
          <div className="flex-1">
            <Label className="text-sm font-normal">Date Type</Label>
            <Select
              options={dateTypes}
              value={selectedDateType}
              onChange={handleDateTypeChange}
            />
          </div>
          <div className="flex-1">
            <Label className="text-sm font-normal">
              {data?.startDate || "Period Start Date"}
              <span className="text-red-500 ml-0.5">*</span>
            </Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setSelectedDateType("Custom Interval");
              }}
            />
          </div>
          <div className="flex-1">
            <Label className="text-sm font-normal">
              {data?.endDate || "Period End Date"}
              <span className="text-red-500 ml-0.5">*</span>
            </Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setSelectedDateType("Custom Interval");
              }}
            />
          </div>
        </HStack>

        {/* Plans + Currency */}
        <HStack className="gap-6">
          <div className="flex-1">
            <Label className="text-sm font-normal">Plan Names</Label>
            <MultiSelectV2
              _className="w-full"
              multiple
              placeholder="All plans"
              options={planOptions}
              handleSelection={(selected) => setSelectedPlanNames(selected)}
              optionValues={selectedPlanNames}
            />
          </div>
          <div className="flex-1">
            <Label className="text-sm font-normal">Currency</Label>
            <Select
              options={CURRENCY_OPTIONS}
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            />
          </div>
        </HStack>

        {/* Advanced Parameters toggle */}
        <HStack className="pt-2 items-center gap-2 before:border-b-2 before:border-dashed before:m-auto after:flex-1 after:border-dashed after:content-[''] after:border-b-2 hidden">
          <ButtonPrimary1
            onClick={() => setShowAdvanced((v) => !v)}
            className="flex flex-row bg-transparent border border-solid cursor-pointer border-orange-501 hover:bg-transparent whitespace-nowrap"
          >
            Black-Scholes Parameters
            <Icon
              icon="material-symbols:keyboard-arrow-down-rounded"
              className={`ml-1 transition-transform ${
                showAdvanced ? "rotate-180" : ""
              }`}
            />
          </ButtonPrimary1>
        </HStack>

        {/* Black-Scholes valuation info link */}
        <ValuationLink />

        {showAdvanced && (
          <VStack className="gap-4 py-2">
            {/* Global BS parameters */}
            <p className="text-xs text-gray-500">
              Global defaults — leave blank to use system defaults. Per-grant
              overrides below take precedence.
            </p>
            <HStack className="gap-6 flex-wrap">
              <div className="flex-1 min-w-[140px]">
                <Label className="text-sm font-normal">
                  Volatility (e.g. 0.35)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  placeholder="0.10"
                  value={volatility}
                  onChange={(e) => setVolatility(e.target.value)}
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <Label className="text-sm font-normal">
                  Risk-Free Rate (e.g. 0.065)
                </Label>
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  placeholder="0.065"
                  value={riskFreeRate}
                  onChange={(e) => setRiskFreeRate(e.target.value)}
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <Label className="text-sm font-normal">
                  Expected Dividends (e.g. 0)
                </Label>
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  placeholder="0"
                  value={expectedDividends}
                  onChange={(e) => setExpectedDividends(e.target.value)}
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <Label className="text-sm font-normal">
                  Attrition Rate (e.g. 0.1)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  placeholder="0.10"
                  value={attritionRate}
                  onChange={(e) => setAttritionRate(e.target.value)}
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <Label className="text-sm font-normal">
                  Share Price / FMV Override
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g. 150"
                  value={sharePrice}
                  onChange={(e) => setSharePrice(e.target.value)}
                />
              </div>
            </HStack>

            {/* Per-grant-date overrides */}
            <div className="mt-2">
              <HStack className="items-center justify-between mb-2">
                <Label className="text-sm font-medium">
                  Per-Grant-Date Overrides (optional)
                </Label>
                <ButtonPrimary1
                  onClick={addBsRow}
                  className="text-xs px-3 py-1 border-orange-501 bg-transparent hover:bg-transparent"
                >
                  + Add Row
                </ButtonPrimary1>
              </HStack>
              {bsRows.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left p-2 border border-gray-200 font-medium">
                          Grant Date
                        </th>
                        <th className="text-left p-2 border border-gray-200 font-medium">
                          Volatility
                        </th>
                        <th className="text-left p-2 border border-gray-200 font-medium">
                          Risk-Free Rate
                        </th>
                        <th className="text-left p-2 border border-gray-200 font-medium">
                          Dividends
                        </th>
                        <th className="text-left p-2 border border-gray-200 font-medium">
                          Attrition
                        </th>
                        <th className="text-left p-2 border border-gray-200 font-medium">
                          Share Price
                        </th>
                        <th className="p-2 border border-gray-200"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bsRows.map((row, idx) => (
                        <tr key={idx}>
                          <td className="p-1 border border-gray-200">
                            <input
                              type="date"
                              className="w-full text-xs border-none outline-none bg-transparent"
                              value={row.grantDate}
                              onChange={(e) =>
                                updateBsRow(idx, "grantDate", e.target.value)
                              }
                            />
                          </td>
                          {(
                            [
                              "volatility",
                              "riskFreeRate",
                              "expectedDividends",
                              "attritionRate",
                              "sharePrice",
                            ] as (keyof BsInputByDate)[]
                          ).map((field) => (
                            <td
                              key={field}
                              className="p-1 border border-gray-200"
                            >
                              <input
                                type="number"
                                step="0.001"
                                min="0"
                                className="w-full text-xs border-none outline-none bg-transparent"
                                value={
                                  row[field] !== undefined
                                    ? String(row[field])
                                    : ""
                                }
                                onChange={(e) =>
                                  updateBsRow(idx, field, e.target.value)
                                }
                              />
                            </td>
                          ))}
                          <td className="p-1 border border-gray-200 text-center">
                            <button
                              type="button"
                              onClick={() => removeBsRow(idx)}
                              className="text-red-400 hover:text-red-600"
                            >
                              <Icon icon="mdi:close" width={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </VStack>
        )}

        {/* Schedule Section */}
        <div className="pt-2 border-t border-gray-100">
          <ScheduleSection
            value={scheduleParams}
            onChange={setScheduleParams}
            users={associatedUsers ?? []}
          />
        </div>

        {/* Date Format + PII + Note */}
        <HStack className="justify-end gap-4 mt-2 items-end">
          <div className="w-48">
            <DateFormatSelect value={dateFormat} onChange={setDateFormat} />
          </div>
          <HStack className="gap-2 mb-2">
            <input
              type="checkbox"
              checked={maskPiiData}
              onChange={() => setMaskPiiData((v) => !v)}
              className="text-white accent-orange-501"
            />
            <span className="text-sm">
              Hide all Personally Identifiable Information
            </span>
          </HStack>
        </HStack>
        <HStack className="justify-end">
          <p className="italic text-sm text-gray-500">
            Note: The date filters are inclusive
          </p>
        </HStack>

        {/* Submit */}
        <HStack className="justify-end">
          <ButtonPrimary onClick={handleSubmit} disabled={loading}>
            {loading
              ? "Generating..."
              : scheduleParams && scheduleParams.recipients.length > 0
              ? "Generate & Schedule"
              : "Generate"}
          </ButtonPrimary>
        </HStack>
      </VStack>
    </div>
  );
};

export default ExpenseReportDialog;
